import {
  Box,
  Chip,
  Divider,
  LinearProgress,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import PropTypes from 'prop-types';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';

const STATUS_MAP = {
  available: { bg: '#ECFDF3', color: '#15803D' },
  limited: { bg: '#FFF7ED', color: '#c2bf0c' },
  full: { bg: '#F3F4F6', color: '#d13e24' },
};
const divisionColor = (divisionStatus) => {
  let color = '#22C55E';
  if (divisionStatus === 'full') {
    color = '#EF4444';
  } else if (divisionStatus === 'limited') {
    color = '#F59E0B';
  }
  return color;
};
const DivisionAvailabilityCard = ({ division }) => {//NOSONAR
  const status =
    STATUS_MAP[division?.availability_status] || STATUS_MAP.available;
  const remaining = Number(division?.remaining_spots || 0);
  const capacity = Number(division?.max_teams || 0);
  const filled = Math.max(capacity - remaining, 0);
  const progressValue =
    capacity > 0 ? Math.min((filled / capacity) * 100, 100) : 0;
  const hasStartingPrice =
    division?.starting_from_price !== null &&
    division?.starting_from_price !== undefined &&
    division?.starting_from_price !== '' &&
    Number.isFinite(Number(division?.starting_from_price));
  const startPrice = hasStartingPrice
    ? `$${Number(division.starting_from_price).toFixed(2)}`
    : 'N/A';
  const purchaseOptionCount = Number(division?.purchase_option_count || 0);
  let priceText = 'Pricing unavailable';
  if (hasStartingPrice) {
    if (purchaseOptionCount > 1) {
      priceText = `Starting from: ${startPrice}`;
    } else {
      priceText = `Price: ${startPrice}`;
    }
  }
  const locationText =
    [division?.location_name, division?.address].filter(Boolean).join(' - ') ||
    'Location TBD';
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  return (
    <Box
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: 2,
        p: 2,
        backgroundColor:
          division?.availability_status === 'full' ? '#F9FAFB' : '#FFFFFF',
        opacity: division?.availability_status === 'full' ? 0.85 : 1,
      }}
    >
      {/* Row 1: name + status */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h3' sx={{ fontWeight: 700 }}>
          {division?.division_name}
        </Typography>
        <Chip
          label={
            division?.availability_status === 'full'
              ? 'Full'
              : `${remaining} spots left`
          }
          size='small'
          sx={{
            backgroundColor:
              division?.availability_status === 'limited'
                ? '#FFF7ED'
                : status.bg,
            color:
              division?.availability_status === 'limited'
                ? '#C2410C'
                : status.color,
            fontWeight: 700,
          }}
        />
      </Box>
      <Divider sx={{ mb: 2 }} />

      {/* Row 2: location + age group */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 1,
          ...(isMobile && { minHeight: '72px' }),
        }}
      >
        <Tooltip title={locationText} arrow disableHoverListener={!isMobile}>
          <Typography
            variant='body2'
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              color: '#4B5563',
              minWidth: 0,
              flex: 1,
              overflow: 'hidden',
            }}
          >
            <RoomOutlinedIcon
              fontSize='small'
              sx={{ flexShrink: 0, mt: '2px' }}
            />
            <Box
              component='span'
              sx={{
                ...(isMobile
                  ? {
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }
                  : {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }),
              }}
            >
              {locationText}
            </Box>
          </Typography>
        </Tooltip>

        {division?.age_group && (
          <Typography
            variant='body2'
            sx={{
              color: '#4B5563',
              alignSelf: isMobile ? 'flex-start' : 'auto',
              flexShrink: 0,
            }}
          >
            {division?.age_group || 'N/A'}
          </Typography>
        )}
      </Box>

      {/* Row 3: progress + filled/total */}
      <LinearProgress
        variant='determinate'
        value={progressValue}
        sx={{
          height: 6,
          borderRadius: 4,
          backgroundColor: '#E5E7EB',
          '& .MuiLinearProgress-bar': {
            backgroundColor:divisionColor(division?.availability_status),
          },
        }}
      />
      {/* Row 4: start price left, slots right */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: 1,
        }}
      >
        <Typography variant='body2' sx={{ mt: 0.5 }}>
          <strong>{priceText}</strong>
        </Typography>
        <Typography variant='body2' sx={{ color: '#6B7280', mt: 0.5 }}>
          {filled}/{capacity || 0}
        </Typography>
      </Box>
    </Box>
  );
};

DivisionAvailabilityCard.propTypes = {
  division: PropTypes.object.isRequired,
};

export default DivisionAvailabilityCard;
